import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { SoundStyle } from "@juegossc/shared";
import { playEvent } from "../../lib/audio";

type Props = {
  width: number;
  height: number;
  /** Threshold de % raspado para auto-revelar completamente. */
  revealThreshold?: number;
  /** Llamado cuando se alcanza el threshold. */
  onRevealed?: () => void;
  /** Hex color del overlay raspable (la "moneda"). */
  overlayColor?: string;
  /** Estilo de sonido del shopping. Si no viene, default 'classic'. */
  soundStyle?: SoundStyle;
  /** Migration 040: cap de DPR del canvas (nitidez). Default 2. */
  dprMax?: number;
  children?: React.ReactNode;
};

const RADIUS = 35; // radio del "rascado" en pixels
// Throttle para el sonido de rascado: cada SCRATCH_THROTTLE_MS un trigger.
// Sin throttle, scratchAt se llama en cada pointermove (60+/s) y el audio
// se solapa de manera incómoda. 70ms da una textura granulada agradable.
const SCRATCH_THROTTLE_MS = 70;

// Panel raspable: canvas overlay encima del contenido (children).
// El usuario "rasca" con mouse/touch y se va revelando lo de abajo.
// Tras revealThreshold% raspado, dispara onRevealed (caller decide qué hacer).
// Threshold default 40%. + idle timer 1.2s tras soltar dedo dispara
// reveal si raspó >=8% (defensa contra cliente que abandona o tarda).
// El 8% es ~1 swipe completo en un panel grande de 400×300 — apenas
// suficiente para "ver" el premio. Si subimos esto, el cliente tiene
// que insistir más; si lo bajamos, se revela con cualquier toque
// (queda raro). 8% es el punto dulce probado en el shopping.
const IDLE_REVEAL_MS = 1200;
const IDLE_MIN_PCT = 0.08;

export function ScratchPanel({
  width,
  height,
  revealThreshold = 0.4,
  onRevealed,
  overlayColor = "#c89a00",
  soundStyle = "classic",
  dprMax = 2,
  children,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const revealedRef = useRef(false);
  const idleTimerRef = useRef<number | null>(null);
  const lastPctRef = useRef(0);
  const lastScratchSoundRef = useRef(0);
  const [revealed, setRevealed] = useState(false);

  function maybePlayScratchSound() {
    const now = performance.now();
    if (now - lastScratchSoundRef.current < SCRATCH_THROTTLE_MS) return;
    lastScratchSoundRef.current = now;
    playEvent("raspadita", "scratch", soundStyle);
  }

  // Pintar overlay inicial.
  // willReadFrequently:true: optimiza getImageData (lo llamamos varias
  // veces para medir % raspado). Browser usa CPU rendering en lugar
  // de GPU, más rápido para readbacks frecuentes.
  //
  // useLayoutEffect (no useEffect) — CRÍTICO para que el premio NUNCA
  // sea visible antes de raspar. useEffect corre DESPUÉS del primer
  // paint del browser → entre el paint y el effect, el canvas está
  // transparente y el children (texto del premio) abajo se vería un
  // frame (~16ms). useLayoutEffect corre SINCRÓNICAMENTE antes del
  // primer paint, garantizando que el overlay dorado opaco ya esté
  // pintado en el frame inicial visible.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Clamp del DPR: el overlay raspable (dorado liso + texto) no necesita
    // densidad retina. En kiosko Android dpr 2-3 multiplica x4-x9 los bytes
    // del getImageData y el costo de los fills por raspado. Migration 040: el
    // cap es configurable (dprMax, default 2).
    const dpr = Math.min(window.devicePixelRatio || 1, dprMax);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // Gradient dorado tipo moneda raspable
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#a07700");
    gradient.addColorStop(0.5, overlayColor);
    gradient.addColorStop(1, "#a07700");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Rayitas diagonales encima del gradient — mismo style que las
    // tarjetitas chicas de ScratchElije (repeating-linear-gradient).
    // Esto unifica el "look raspable" entre las 3 mecánicas.
    ctx.strokeStyle = "rgba(0,0,0,0.1)";
    ctx.lineWidth = 2;
    const STRIPE_GAP = 8;
    for (let x = -height; x < width; x += STRIPE_GAP) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + height, height);
      ctx.stroke();
    }

    // Texto "RASPÁ AQUÍ" en el overlay (encima de las rayitas)
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.font = `bold ${Math.min(width, height) * 0.08}px "Nunito", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("RASPÁ AQUÍ", width / 2, height / 2);
  }, [width, height, overlayColor, dprMax]);

  function getCanvasPoint(
    canvas: HTMLCanvasElement,
    clientX: number,
    clientY: number,
  ) {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * width,
      y: ((clientY - rect.top) / rect.height) * height,
    };
  }

  function scratchAt(x: number, y: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, RADIUS, 0, Math.PI * 2);
    ctx.fill();
    maybePlayScratchSound();
    // NO chequeamos reveal acá. Si chequeáramos durante el raspado, el
    // modal saltaría mientras el cliente sigue rascando, interrumpiéndolo.
    // El reveal se dispara solo cuando soltás (mouseUp/touchEnd), te vas
    // del canvas (mouseLeave) o por idle timer (1.2s sin actividad).
  }

  // Mide % raspado y revela si supera el threshold (o si force=true
  // por idle y ya raspó al menos IDLE_MIN_PCT).
  // Threshold del pixel: alpha < 128 cuenta como "raspado". Usar === 0
  // estricto no andaba: los pixels del borde del arc tienen anti-aliasing
  // (alpha intermedio, ej 100, 180), nunca son exactamente 0. Con < 128
  // capturamos tanto el centro del arc (alpha=0) como los bordes.
  function checkRevealed(force = false) {
    if (revealedRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let cleared = 0;
    let total = 0;
    // Muestreo: leemos cada 10 pixels físicos. step en BYTES = 10 * 4.
    const step = 10 * 4;
    for (let i = 3; i < data.length; i += step) {
      total++;
      if (data[i] < 128) cleared++;
    }
    const pct = total > 0 ? cleared / total : 0;
    lastPctRef.current = pct;
    const shouldReveal =
      pct >= revealThreshold || (force && pct >= IDLE_MIN_PCT);
    if (shouldReveal) {
      revealedRef.current = true;
      // setTransform identity antes del fillRect: el ctx tiene scale
      // aplicado del useEffect inicial, fillRect(0,0,canvas.width,...)
      // usa pixels físicos. Sin reset, se escala doble y deja zonas
      // sin limpiar en monitores con dpr > 1.
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      setRevealed(true);
      // Refactor sonidos 2026-06-02: sonido de revelación cuando el
      // canvas termina de despejarse, antes del modal del premio.
      playEvent("raspadita", "reveal", soundStyle);
      onRevealed?.();
    }
  }

  // Resetea el timer de idle. Si pasa IDLE_REVEAL_MS sin más raspado,
  // dispara checkRevealed(force=true) — útil cuando el cliente ya
  // descubrió suficiente para ver el premio pero no llegó al threshold.
  function resetIdleTimer() {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = window.setTimeout(() => {
      checkRevealed(true);
    }, IDLE_REVEAL_MS);
  }

  // Cleanup del timer al unmount (evita timer "huérfano" si el
  // componente se desmonta antes del idle).
  useEffect(() => {
    return () => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative overflow-hidden rounded-2xl shadow-2xl"
      style={{
        width,
        height,
        background: "#1a1540",
        // touch-action none + overscroll contain: el dedo sobre el area de
        // raspar no scrollea la pagina ni dispara pull-to-refresh.
        touchAction: "none",
        overscrollBehavior: "contain",
      }}
    >
      {/* Contenido (premio) debajo, queda invisible bajo el canvas */}
      <div className="absolute inset-0 z-0 flex items-center justify-center p-6 text-center">
        {children}
      </div>

      {/* Canvas overlay raspable ENCIMA del premio (z-10) — si no
          tuviera position:absolute, queda debajo del div premio y los
          eventos del mouse no llegan al canvas. */}
      <canvas
        ref={canvasRef}
        style={{
          width,
          height,
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 10,
          touchAction: "none",
          cursor: revealed ? "default" : "crosshair",
          display: revealed ? "none" : "block",
        }}
        onMouseDown={(e) => {
          isDrawingRef.current = true;
          const canvas = canvasRef.current;
          if (!canvas) return;
          const { x, y } = getCanvasPoint(canvas, e.clientX, e.clientY);
          scratchAt(x, y);
          resetIdleTimer();
        }}
        onMouseMove={(e) => {
          if (!isDrawingRef.current) return;
          const canvas = canvasRef.current;
          if (!canvas) return;
          const { x, y } = getCanvasPoint(canvas, e.clientX, e.clientY);
          scratchAt(x, y);
          resetIdleTimer();
        }}
        onMouseUp={() => {
          isDrawingRef.current = false;
          checkRevealed();
          resetIdleTimer();
        }}
        onMouseLeave={() => {
          isDrawingRef.current = false;
          // Si el cliente arrastra el mouse fuera del canvas mientras
          // raspaba (común en tablets con stylus o si se distrae), el
          // mouseUp puede dispararse en otro elemento y nunca llegar
          // acá. Chequeamos ya con force=true (no esperamos al idle).
          checkRevealed(true);
          resetIdleTimer();
        }}
        onTouchStart={(e) => {
          isDrawingRef.current = true;
          const canvas = canvasRef.current;
          if (!canvas || !e.touches[0]) return;
          const t = e.touches[0];
          const { x, y } = getCanvasPoint(canvas, t.clientX, t.clientY);
          scratchAt(x, y);
          resetIdleTimer();
        }}
        onTouchMove={(e) => {
          if (!isDrawingRef.current) return;
          const canvas = canvasRef.current;
          if (!canvas || !e.touches[0]) return;
          const t = e.touches[0];
          const { x, y } = getCanvasPoint(canvas, t.clientX, t.clientY);
          scratchAt(x, y);
          resetIdleTimer();
        }}
        onTouchEnd={() => {
          isDrawingRef.current = false;
          checkRevealed();
          resetIdleTimer();
        }}
      />
    </div>
  );
}
